import { errorMessage } from "@streamoid/errors";
import React, { useEffect, useState } from "react";
import { notify } from "../../utils/utils";
import {
  createUserSpecialization,
  deleteUserSpecialization,
  getManageUserSpecializations,
  resetUserSpecializationsToDefaults,
  updateUserSpecialization,
} from "../../utils/userSpecializations";
import "./style.css";

const emptyForm = {
  key: "",
  label: "",
  description: "",
  iconUrl: "",
  active: true,
  sortOrder: "0",
};

export default function UserSpecializations() {
  const [specializations, setSpecializations] = useState<any[]>([]);
  const [formData, setFormData] = useState(emptyForm);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  const fetchSpecializations = () => {
    setIsLoading(true);
    getManageUserSpecializations()
      .then((res) => {
        setSpecializations(res?.data || []);
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchSpecializations();
  }, []);

  const updateDraft = (specializationId: string, updates: Record<string, any>) => {
    setSpecializations((currentSpecializations) =>
      currentSpecializations.map((specialization) =>
        specialization._id === specializationId
          ? { ...specialization, ...updates }
          : specialization,
      ),
    );
  };

  const handleCreate = () => {
    const key = formData.key.trim();
    const label = formData.label.trim();

    if (!key || !label) {
      notify("Key and label are required", "error");
      return;
    }

    setIsCreating(true);
    createUserSpecialization({
      key,
      label,
      description: formData.description.trim(),
      iconUrl: formData.iconUrl.trim(),
      active: formData.active,
      sortOrder: Number(formData.sortOrder || 0),
    })
      .then(() => {
        notify("User specialization created successfully");
        setFormData(emptyForm);
        fetchSpecializations();
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setIsCreating(false);
      });
  };

  const handleSave = (specialization: any) => {
    setSavingId(specialization._id);
    updateUserSpecialization(specialization._id, {
      label: specialization.label.trim(),
      description: (specialization.description || "").trim(),
      iconUrl: (specialization.iconUrl || "").trim(),
      active: Boolean(specialization.active),
      sortOrder: Number(specialization.sortOrder || 0),
    })
      .then(() => {
        notify("User specialization updated successfully");
        fetchSpecializations();
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setSavingId(null);
      });
  };

  const handleDelete = (specialization: any) => {
    const confirmed = window.confirm(
      `Delete specialization "${specialization.label}" (${specialization.key})?`,
    );
    if (!confirmed) return;

    setDeletingId(specialization._id);
    deleteUserSpecialization(specialization._id)
      .then(() => {
        notify("User specialization deleted successfully");
        fetchSpecializations();
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setDeletingId(null);
      });
  };

  const handleResetToDefaults = () => {
    const confirmed = window.confirm(
      "Reset all specialization entries to the default list? This will delete non-default entries.",
    );
    if (!confirmed) return;

    setIsResetting(true);
    resetUserSpecializationsToDefaults()
      .then(() => {
        notify("Specializations reset to defaults.");
        fetchSpecializations();
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setIsResetting(false);
      });
  };

  return (
    <div className="user-specialization-page">
      <div className="dashboard-settings-section">
        <div className="section-heading">User Specialization</div>
        <div className="settings-container user-specialization-panel">
          <div className="version-header">
            <div className="version-title">Create specialization</div>
            <div className="version-subtitle">
              Add stable keys here. Existing keys remain the stored user value.
            </div>
          </div>

          <div className="user-specialization-reset-actions">
            <button
              className="user-specialization-reset-btn"
              onClick={handleResetToDefaults}
              disabled={isResetting}
            >
              {isResetting ? "Resetting..." : "Reset to defaults"}
            </button>
          </div>

          <div className="user-specialization-form-grid">
            <input
              className="version-input user-specialization-field user-specialization-field-key"
              value={formData.key}
              placeholder="key e.g. design_ops"
              onChange={(e) =>
                setFormData((current) => ({
                  ...current,
                  key: e.target.value,
                }))
              }
              disabled={isCreating}
            />
            <input
              className="version-input user-specialization-field user-specialization-field-label"
              value={formData.label}
              placeholder="label e.g. Design Ops"
              onChange={(e) =>
                setFormData((current) => ({
                  ...current,
                  label: e.target.value,
                }))
              }
              disabled={isCreating}
            />
            <textarea
              className="user-specialization-textarea user-specialization-field user-specialization-field-description"
              value={formData.description}
              placeholder="description"
              onChange={(e) =>
                setFormData((current) => ({
                  ...current,
                  description: e.target.value,
                }))
              }
              disabled={isCreating}
            />
            <input
              className="version-input user-specialization-field user-specialization-field-icon"
              value={formData.iconUrl}
              placeholder="icon URL"
              onChange={(e) =>
                setFormData((current) => ({
                  ...current,
                  iconUrl: e.target.value,
                }))
              }
              disabled={isCreating}
            />
            <input
              className="version-input user-specialization-field user-specialization-field-sort"
              type="number"
              min="0"
              value={formData.sortOrder}
              placeholder="Sort order"
              onChange={(e) =>
                setFormData((current) => ({
                  ...current,
                  sortOrder: e.target.value,
                }))
              }
              disabled={isCreating}
            />
            <label className="user-specialization-active-toggle user-specialization-field user-specialization-field-active">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) =>
                  setFormData((current) => ({
                    ...current,
                    active: e.target.checked,
                  }))
                }
                disabled={isCreating}
              />
              <span>Active</span>
            </label>
          </div>

          <div className="user-specialization-form-actions">
            <button
              className="version-save-btn"
              onClick={handleCreate}
              disabled={isCreating}
            >
              {isCreating ? "Saving..." : "Add specialization"}
            </button>
          </div>
        </div>
      </div>

      <div className="dashboard-settings-section">
        <div className="section-heading">Existing Entries</div>
        <div className="user-specialization-list">
          {isLoading ? (
            <div className="settings-container">Loading specializations...</div>
          ) : !specializations.length ? (
            <div className="settings-container">No specializations found.</div>
          ) : (
            specializations.map((specialization) => (
              <div
                key={specialization._id}
                className="settings-container user-specialization-card"
              >
                <div className="user-specialization-card-header">
                  <div>
                    <div className="summary-label">Stored key</div>
                    <div className="user-specialization-key">
                      {specialization.key}
                    </div>
                  </div>
                  <div className="user-specialization-card-actions">
                    <button
                      className="user-specialization-delete-btn"
                      onClick={() => handleDelete(specialization)}
                      disabled={
                        deletingId === specialization._id ||
                        savingId === specialization._id
                      }
                    >
                      {deletingId === specialization._id ? "Deleting..." : "Delete"}
                    </button>
                    <button
                      className="version-save-btn"
                      onClick={() => handleSave(specialization)}
                      disabled={
                        savingId === specialization._id ||
                        deletingId === specialization._id
                      }
                    >
                      {savingId === specialization._id ? "Saving..." : "Update"}
                    </button>
                  </div>
                </div>

                <div className="user-specialization-form-grid">
                  <input
                    className="version-input user-specialization-field user-specialization-field-label"
                    value={specialization.label}
                    placeholder="Label"
                    onChange={(e) =>
                      updateDraft(specialization._id, {
                        label: e.target.value,
                      })
                    }
                  />
                  <textarea
                    className="user-specialization-textarea user-specialization-field user-specialization-field-description"
                    value={specialization.description || ""}
                    placeholder="Description"
                    onChange={(e) =>
                      updateDraft(specialization._id, {
                        description: e.target.value,
                      })
                    }
                  />
                  <input
                    className="version-input user-specialization-field user-specialization-field-icon"
                    value={specialization.iconUrl || ""}
                    placeholder="Icon URL"
                    onChange={(e) =>
                      updateDraft(specialization._id, {
                        iconUrl: e.target.value,
                      })
                    }
                  />
                  <input
                    className="version-input user-specialization-field user-specialization-field-sort"
                    type="number"
                    min="0"
                    value={specialization.sortOrder}
                    placeholder="Sort order"
                    onChange={(e) =>
                      updateDraft(specialization._id, {
                        sortOrder: e.target.value,
                      })
                    }
                  />
                  <label className="user-specialization-active-toggle user-specialization-field user-specialization-field-active">
                    <input
                      type="checkbox"
                      checked={Boolean(specialization.active)}
                      onChange={(e) =>
                        updateDraft(specialization._id, {
                          active: e.target.checked,
                        })
                      }
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
